import React, { useEffect, useRef } from "react";
import CloseIcon from "@src/assets/svg/close-icon.svg";
import { Button } from "@src/components/atoms/buttons/button/button";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import styles from "@src/components/organisms/modals/cleanup-confirm-modal/cleanup-confirm-modal.module.scss";
import { useI18N } from "@src/language/language-context";

const cssClassName = "cleanup-confirm-modal";

export interface CleanupConfirmModalProps {
  gateways: { gatewayId: string; name: string }[];
  isDeleting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const CleanupConfirmModal = ({
  gateways,
  isDeleting,
  onConfirm,
  onClose,
}: CleanupConfirmModalProps) => {
  const i18n = useI18N();
  const dialogRef = useRef<HTMLDivElement>(null);

  const texts = {
    title: i18n("modules.cleanupViewAdminPanel.confirmTitle"),
    description: i18n("modules.cleanupViewAdminPanel.confirmDescription"),
    confirmButton: i18n("modules.cleanupViewAdminPanel.confirmButton"),
    cancelButton: i18n("modules.cleanupViewAdminPanel.cancelButton"),
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }

    const cancelButton =
      dialog.querySelector<HTMLButtonElement>(`[data-autofocus]`);
    cancelButton?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isDeleting) {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
        ),
      ).filter((element) => !element.hasAttribute("disabled"));
      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = dialog.ownerDocument.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    dialog.addEventListener("keydown", handleKeyDown);
    return () => {
      dialog.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDeleting, onClose]);

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__overlay`]} />
      <div
        aria-labelledby={`${cssClassName}-title`}
        aria-modal="true"
        className={styles[`${cssClassName}__dialog`]}
        ref={dialogRef}
        role="dialog"
      >
        <header className={styles[`${cssClassName}__dialog-header`]}>
          <h2 id={`${cssClassName}-title`}>{texts.title}</h2>
          <div className={styles[`${cssClassName}__close-button`]}>
            <IconButton fun={onClose} icon={CloseIcon} />
          </div>
        </header>
        <div className={styles[`${cssClassName}__divider`]} />
        <p className={styles[`${cssClassName}__confirm-description`]}>
          {texts.description}
        </p>
        <ul className={styles[`${cssClassName}__confirm-list`]}>
          {gateways.map((gateway) => (
            <li key={gateway.gatewayId}>{gateway.name}</li>
          ))}
        </ul>
        <footer className={styles[`${cssClassName}__dialog-footer`]}>
          <div className={styles[`${cssClassName}__divider`]} />
          <div className={styles[`${cssClassName}__dialog-actions`]}>
            <Button
              type="button"
              text={texts.confirmButton}
              variant="primary"
              disabled={isDeleting}
              fun={onConfirm}
            />
            <Button
              data-autofocus=""
              type="button"
              text={texts.cancelButton}
              variant="secondary"
              disabled={isDeleting}
              fun={onClose}
            />
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CleanupConfirmModal;
